import { useState, useEffect } from "react";
import { supabase } from "../../../lib/supabase";
import { Globe, Plus, X, Check, Trash, ChevronDown, ChevronUp, Filter } from "lucide-react";

interface Scope {
  code: string;
  name: string;
}

interface WebhookEntry {
  object_type: string;
  url: string;
}

type WebhookData = Record<string, string>[];

interface WebhookHistory {
  id: string;
  saas_id: string;
  integration_id: string | null;
  hotel_code: string | null;
  webhook_url: string;
  entity_type: string;
  payload: any;
  status_code: number | null;
  created_at: string;
  response_body: string | null;
  error_message: string | null;
}

export function Webhooks() {
  const [scopes, setScopes] = useState<Scope[]>([]);
  const [webhooks, setWebhooks] = useState<WebhookEntry[]>([]);
  const [availableObjectTypes, setAvailableObjectTypes] = useState<Scope[]>([]);
  const [newWebhook, setNewWebhook] = useState<WebhookEntry>({
    object_type: "",
    url: "",
  });
  const [isAddingWebhook, setIsAddingWebhook] = useState(false);
  const [editingWebhookIndex, setEditingWebhookIndex] = useState<number | null>(null);
  const [editingWebhookUrl, setEditingWebhookUrl] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // History section states
  const [history, setHistory] = useState<WebhookHistory[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedHistoryId, setExpandedHistoryId] = useState<string | null>(null);
  const [historyFilters, setHistoryFilters] = useState({
    entityType: "",
    statusCode: "",
    startDate: "",
    endDate: "",
    hotelCode: "",
  });
  const [showFilters, setShowFilters] = useState(false);
  const [availableHotelCodes, setAvailableHotelCodes] = useState<string[]>([]);

  useEffect(() => {
    loadScopes();
    loadWebhooks();
    loadHistory();
    loadAvailableHotelCodes();
  }, []);

  const loadAvailableHotelCodes = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      // Fetch all hotel codes for this SaaS to populate the filter dropdown
      const { data, error } = await supabase
        .from("webhook_history")
        .select("hotel_code")
        .eq("saas_id", user.id)
        .not("hotel_code", "is", null);

      if (error) throw error;

      const uniqueHotelCodes = Array.from(
        new Set(
          (data || [])
            .map((item) => item.hotel_code)
            .filter((code): code is string => code !== null && code !== "")
        )
      ).sort();
      setAvailableHotelCodes(uniqueHotelCodes);
    } catch (err) {
      console.error("Error loading available hotel codes:", err);
    }
  };

  useEffect(() => {
    // Update available object types when webhooks change
    const usedTypes = webhooks.map((webhook) => webhook.object_type);
    const available = scopes.filter(
      (scope) => !usedTypes.includes(scope.code) && scope.code.toLowerCase() !== "webhooks"
    );
    setAvailableObjectTypes(available);
  }, [scopes, webhooks]);

  const loadScopes = async () => {
    try {
      const restrictedScopes = [
        "Reservations",
        "Guests",
        "Hotel Configuration",
        "Products and Services",
        "Rates",
        "Rooms",
        "Webhooks",
      ];

      const { data: scopesData, error: scopesError } = await supabase
        .from("pms_scopes")
        .select("id, code, name")
        .in("name", restrictedScopes)
        .order("name");

      if (scopesError) throw scopesError;

      const transformedScopes = (scopesData || []).map((scope) => ({
        // Use normalized name (lowercase with underscores) as the code for webhooks
        // This ensures webhooks are stored as "reservations", "guests", etc.
        code: scope.name.toLowerCase().replace(/\s+/g, "_"),
        name: scope.name,
      }));

      setScopes(transformedScopes);
    } catch (err) {
      console.error("Error loading scopes:", err);
      setError("Failed to load scopes");
    } finally {
      setLoading(false);
    }
  };

  const loadWebhooks = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No authenticated user found");

      const { data: saasDetails, error: saasError } = await supabase
        .from("saas_details")
        .select("webhooks")
        .eq("id", user.id)
        .single();

      if (saasError) throw saasError;

      if (saasDetails?.webhooks && Array.isArray(saasDetails.webhooks) && saasDetails.webhooks.length > 0) {
        const webhookEntries: WebhookEntry[] = [];
        saasDetails.webhooks.forEach((entry: Record<string, string>) => {
          const objectType = Object.keys(entry)[0];
          const url = entry[objectType];
          if (objectType && url) {
            webhookEntries.push({
              object_type: objectType,
              url: url,
            });
          }
        });
        console.log("Loaded webhooks from database:", webhookEntries);
        setWebhooks(webhookEntries);
      } else {
        console.log("No webhooks found in database or webhooks is null/empty");
        setWebhooks([]);
      }
    } catch (err) {
      console.error("Error loading webhooks:", err);
      setError("Failed to load webhooks");
    }
  };

  const loadHistory = async () => {
    try {
      setHistoryLoading(true);
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No authenticated user found");

      let query = supabase
        .from("webhook_history")
        .select("*")
        .eq("saas_id", user.id)
        .order("created_at", { ascending: false })
        .limit(100);

      // Apply filters
      if (historyFilters.entityType) {
        query = query.eq("entity_type", historyFilters.entityType);
      }
      if (historyFilters.statusCode) {
        query = query.eq("status_code", parseInt(historyFilters.statusCode));
      }
      if (historyFilters.startDate) {
        query = query.gte("created_at", historyFilters.startDate);
      }
      if (historyFilters.endDate) {
        query = query.lte("created_at", historyFilters.endDate);
      }
      if (historyFilters.hotelCode) {
        query = query.eq("hotel_code", historyFilters.hotelCode);
      }

      const { data, error: historyError } = await query;

      if (historyError) throw historyError;
      setHistory(data || []);

      // Extract unique hotel codes for filter dropdown
      const uniqueHotelCodes = Array.from(
        new Set(
          (data || [])
            .map((item) => item.hotel_code)
            .filter((code): code is string => code !== null && code !== "")
        )
      ).sort();
      setAvailableHotelCodes(uniqueHotelCodes);
    } catch (err) {
      console.error("Error loading webhook history:", err);
      setError("Failed to load webhook history");
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleAddWebhook = () => {
    setIsAddingWebhook(true);
    setNewWebhook({
      object_type: availableObjectTypes.length > 0 ? availableObjectTypes[0].code : "",
      url: "",
    });
  };

  const handleCancelAddWebhook = () => {
    setIsAddingWebhook(false);
    setNewWebhook({ object_type: "", url: "" });
  };

  const handleSaveWebhook = () => {
    if (!newWebhook.object_type || !newWebhook.url) {
      setError("Please select an entity type and enter a webhook URL");
      return;
    }

    // Validate URL
    try {
      new URL(newWebhook.url);
    } catch {
      setError("Please enter a valid URL");
      return;
    }

    setWebhooks([...webhooks, newWebhook]);
    setIsAddingWebhook(false);
    setNewWebhook({ object_type: "", url: "" });
    setError("");
  };

  const handleRemoveWebhook = (index: number) => {
    setWebhooks(webhooks.filter((_, i) => i !== index));
    if (editingWebhookIndex === index) {
      setEditingWebhookIndex(null);
      setEditingWebhookUrl("");
    }
  };

  const handleEditWebhook = (index: number) => {
    setEditingWebhookIndex(index);
    setEditingWebhookUrl(webhooks[index].url);
  };

  const handleCancelEditWebhook = () => {
    setEditingWebhookIndex(null);
    setEditingWebhookUrl("");
  };

  const handleSaveEditWebhook = () => {
    if (editingWebhookIndex === null) return;

    if (!editingWebhookUrl.trim()) {
      setError("Please enter a valid webhook URL");
      return;
    }

    // Validate URL
    try {
      new URL(editingWebhookUrl);
    } catch {
      setError("Please enter a valid URL");
      return;
    }

    const updatedWebhooks = [...webhooks];
    updatedWebhooks[editingWebhookIndex] = {
      ...updatedWebhooks[editingWebhookIndex],
      url: editingWebhookUrl,
    };
    setWebhooks(updatedWebhooks);
    setEditingWebhookIndex(null);
    setEditingWebhookUrl("");
    setError("");
  };

  const syncWebhooksToIntegrations = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No authenticated user found");

      // Get current saas_details.webhooks
      const { data: saasDetails, error: saasError } = await supabase
        .from("saas_details")
        .select("webhooks")
        .eq("id", user.id)
        .single();

      if (saasError) throw saasError;

      const saasWebhooks = saasDetails?.webhooks || [];
      if (!Array.isArray(saasWebhooks) || saasWebhooks.length === 0) {
        // If no webhooks, clear all integration webhooks and remove 'webhooks' from scopes
        // First get all integrations that have webhooks to update their scopes
        const { data: integrationsToClear, error: fetchError } = await supabase
          .from("integrations")
          .select("id, scopes")
          .eq("saas_id", user.id)
          .not("webhooks", "is", null);

        if (fetchError) throw fetchError;

        // Update each integration to clear webhooks and remove 'webhooks' from scopes
        if (integrationsToClear && integrationsToClear.length > 0) {
          for (const integration of integrationsToClear) {
            const currentScopes = integration.scopes || [];
            const updatedScopes = currentScopes.filter((scope: string) => scope.toLowerCase() !== "webhooks");
            
            const { error: clearError } = await supabase
              .from("integrations")
              .update({ 
                webhooks: null,
                scopes: updatedScopes
              })
              .eq("id", integration.id);

            if (clearError) {
              console.error(`Error clearing webhooks for integration ${integration.id}:`, clearError);
            }
          }
        }
        return;
      }

      // Get all active integrations (not just those with webhooks)
      // We need to update all of them to sync webhooks and scopes
      const { data: integrations, error: integrationsError } = await supabase
        .from("integrations")
        .select("id, hotel_id, webhooks, scopes")
        .eq("saas_id", user.id)
        .eq("status", "active");

      if (integrationsError) throw integrationsError;

      if (!integrations || integrations.length === 0) {
        return; // No integrations to update
      }

      // Get hotel IDs and their connector codes
      const hotelIds = integrations.map((i) => i.hotel_id).filter(Boolean);
      const { data: credentials, error: credentialsError } = await supabase
        .from("credentials")
        .select("hotel_id, connector_code")
        .in("hotel_id", hotelIds)
        .eq("connector_type", "pms");

      if (credentialsError) throw credentialsError;

      // Create map of hotel_id to connector_code
      const hotelConnectorMap = new Map<string, string>();
      (credentials || []).forEach((cred) => {
        if (cred.connector_code) {
          hotelConnectorMap.set(cred.hotel_id, cred.connector_code);
        }
      });

      // Get unique connector codes
      const connectorCodes = Array.from(
        new Set(hotelConnectorMap.values())
      ).filter(Boolean);

      // Get connectors with trigger_objects
      const { data: connectors, error: connectorsError } = await supabase
        .from("connectors")
        .select("code, trigger_objects")
        .in("code", connectorCodes);

      if (connectorsError) throw connectorsError;

      // Create map of connector_code to trigger_objects
      const connectorTriggerMap = new Map<string, string[]>();
      (connectors || []).forEach((connector) => {
        connectorTriggerMap.set(
          connector.code,
          connector.trigger_objects || []
        );
      });

      // Load scope code to normalized name mapping for webhook filtering
      // trigger_objects are stored as scope codes (e.g., "reservations")
      // webhooks are stored as normalized names (e.g., "reservations")
      const { data: scopesData, error: scopesError } = await supabase
        .from("pms_scopes")
        .select("code, name");

      const scopeCodeToNameMap = new Map<string, string>();
      const normalizedNameToCodeMap = new Map<string, string>(); // Reverse map: normalized name -> code
      if (!scopesError && scopesData) {
        scopesData.forEach((scope) => {
          const normalizedName = scope.name.toLowerCase().replace(/\s+/g, "_");
          scopeCodeToNameMap.set(scope.code, normalizedName);
          normalizedNameToCodeMap.set(normalizedName, scope.code);
        });
      }

      // Standardization mapping: convert old scope codes to new standardized names
      // This aligns with backend standardization: booking -> reservations, customer -> guests
      const standardizationMap: Record<string, string> = {
        "booking": "reservations",
        "bookings": "reservations",
        "customer": "guests",
        "customer_data": "guests",
        "customers": "guests",
      };

      // Update each integration
      for (const integration of integrations) {
        const connectorCode = hotelConnectorMap.get(integration.hotel_id);
        if (!connectorCode) {
          console.log(`No connector code found for integration ${integration.id}, hotel ${integration.hotel_id}`);
          continue;
        }

        const triggerObjects = connectorTriggerMap.get(connectorCode) || [];
        console.log(`Integration ${integration.id}: connector=${connectorCode}, trigger_objects=[${triggerObjects.join(", ")}]`);

        // Get integration scopes - we'll use these to supplement trigger_objects
        const integrationScopes = integration.scopes || [];
        console.log(`  Integration scopes: [${integrationScopes.join(", ")}]`);
        console.log(`  SaaS webhooks available:`, saasWebhooks);

        // Convert trigger_objects from scope codes to normalized names for comparison
        // trigger_objects are stored as scope codes (e.g., "reservations", "customer")
        // webhooks are stored as normalized names (e.g., "reservations", "guests")
        let triggerObjectsNormalized: string[] = [];
        
        // Convert connector trigger_objects to normalized names
        const connectorTriggerObjectsNormalized = triggerObjects.map((code) => {
          // First try standardization map (for old codes like "customer" -> "guests")
          if (standardizationMap[code]) {
            const normalized = standardizationMap[code];
            console.log(`  Converting trigger_object "${code}" via standardization map to "${normalized}"`);
            return normalized;
          }
          // Then try scope code to name map, then use code as-is
          let normalized = scopeCodeToNameMap.get(code) || code;
          // Apply standardization to the normalized name as well (in case it's "bookings" -> "reservations")
          if (standardizationMap[normalized]) {
            normalized = standardizationMap[normalized];
            console.log(`  Converting normalized name "${scopeCodeToNameMap.get(code) || code}" via standardization map to "${normalized}"`);
          } else {
            console.log(`  Converting trigger_object "${code}" to normalized name "${normalized}"`);
          }
          return normalized;
        });
        
        // Convert integration scopes to normalized names
        const integrationScopesNormalized = integrationScopes
          .filter((scope) => scope.toLowerCase() !== "webhooks") // Exclude webhooks scope
          .map((scope) => {
            // First check if scope is already a normalized name (matches a webhook object type)
            const webhookObjectTypes = saasWebhooks.map((w: Record<string, string>) => Object.keys(w)[0]);
            console.log(`  Processing scope "${scope}" (type: ${typeof scope})`);
            console.log(`    Available webhook object types: [${webhookObjectTypes.join(", ")}]`);
            console.log(`    Is "${scope}" in webhook object types? ${webhookObjectTypes.includes(scope)}`);
            
            if (webhookObjectTypes.includes(scope)) {
              console.log(`  ✓ Scope "${scope}" already matches webhook object type - using directly`);
              return scope;
            }
            
            // Check if it's in standardization map (old codes like "customer" -> "guests")
            if (standardizationMap[scope]) {
              const normalized = standardizationMap[scope];
              console.log(`  Converting scope "${scope}" via standardization map to "${normalized}"`);
              return normalized;
            }
            
            // Check if it's a scope code that needs conversion
            let normalized = scopeCodeToNameMap.get(scope);
            if (normalized) {
              console.log(`  Converting scope code "${scope}" to normalized name "${normalized}"`);
              // Apply standardization to normalized name if needed
              if (standardizationMap[normalized]) {
                normalized = standardizationMap[normalized];
                console.log(`  Applying standardization to "${scopeCodeToNameMap.get(scope)}" -> "${normalized}"`);
              }
              return normalized;
            }
            
            // If not found in maps, check if it's already a normalized name by checking reverse map
            if (normalizedNameToCodeMap.has(scope)) {
              console.log(`  Scope "${scope}" is already a normalized name`);
              return scope;
            }
            
            // Last resort: use as-is (might already be normalized)
            console.log(`  Using scope "${scope}" as-is (assuming it's already normalized)`);
            return scope;
          })
          .filter((normalized) => normalized !== undefined && normalized !== null);
        
        // Use the UNION of connector trigger_objects AND integration scopes
        // This ensures we include webhooks for all scopes the integration supports
        // even if the connector's trigger_objects is limited
        triggerObjectsNormalized = Array.from(
          new Set([...connectorTriggerObjectsNormalized, ...integrationScopesNormalized])
        );
        
        if (triggerObjects.length === 0) {
          // If trigger_objects is empty, use only integration scopes
          triggerObjectsNormalized = integrationScopesNormalized;
          console.log(`  No trigger_objects found, using integration scopes only: [${triggerObjectsNormalized.join(", ")}]`);
        } else {
          // If trigger_objects exists, use UNION of both (connector trigger_objects + integration scopes)
          console.log(`  Using UNION of connector trigger_objects [${connectorTriggerObjectsNormalized.join(", ")}] and integration scopes [${integrationScopesNormalized.join(", ")}]`);
          console.log(`  Final normalized trigger_objects: [${triggerObjectsNormalized.join(", ")}]`);
        }

        console.log(`  Normalized trigger_objects: [${triggerObjectsNormalized.join(", ")}]`);
        const webhookObjectTypes = saasWebhooks.map((w: Record<string, string>) => Object.keys(w)[0]);
        console.log(`  Available SaaS webhooks:`, webhookObjectTypes);

        // Filter webhooks to only include entries for objects that match trigger_objects
        // Webhooks are stored with normalized scope names (e.g., "reservations", "guests")
        console.log(`  About to filter webhooks. triggerObjectsNormalized: [${triggerObjectsNormalized.join(", ")}]`);
        const filteredWebhooks = saasWebhooks.filter(
          (webhookEntry: Record<string, string>) => {
            const objectType = Object.keys(webhookEntry)[0]; // e.g., "reservations", "guests"
            
            // Direct match
            if (triggerObjectsNormalized.includes(objectType)) {
              console.log(`  ✓ Webhook "${objectType}" matches trigger_objects: true (direct match)`);
              return true;
            }
            
            // Check if objectType matches any normalized trigger object (case-insensitive)
            const matches = triggerObjectsNormalized.some((normalized) => 
              normalized.toLowerCase() === objectType.toLowerCase()
            );
            
            if (matches) {
              console.log(`  ✓ Webhook "${objectType}" matches trigger_objects: true (case-insensitive match)`);
            } else {
              console.log(`  ✗ Webhook "${objectType}" does NOT match any of [${triggerObjectsNormalized.join(", ")}]`);
            }
            return matches;
          }
        );

        console.log(`  Filtered webhooks for integration ${integration.id} (${filteredWebhooks.length} webhooks):`, filteredWebhooks);

        // Get current scopes to update them
        const currentScopes = integration.scopes || [];
        let updatedScopes = [...currentScopes];
        
        // If webhooks are being set, ensure 'webhooks' is in scopes
        // If webhooks are being cleared, remove 'webhooks' from scopes
        if (filteredWebhooks.length > 0) {
          // Add 'webhooks' if not present
          if (!updatedScopes.includes("webhooks")) {
            updatedScopes.push("webhooks");
          }
        } else {
          // Remove 'webhooks' if present
          updatedScopes = updatedScopes.filter((scope: string) => scope.toLowerCase() !== "webhooks");
        }

        // Update integration webhooks and scopes
        console.log(`  Updating integration ${integration.id} with webhooks:`, filteredWebhooks);
        const { data: updateData, error: updateError } = await supabase
          .from("integrations")
          .update({
            webhooks:
              filteredWebhooks.length > 0 ? filteredWebhooks : null,
            scopes: updatedScopes,
          })
          .eq("id", integration.id)
          .select("webhooks, scopes");

        if (updateError) {
          console.error(
            `✗ Error updating integration ${integration.id}:`,
            updateError
          );
        } else {
          console.log(`  ✓ Successfully updated integration ${integration.id}`);
          console.log(`    Updated webhooks:`, updateData?.[0]?.webhooks);
          console.log(`    Updated scopes:`, updateData?.[0]?.scopes);
        }
      }
    } catch (err) {
      console.error("Error syncing webhooks to integrations:", err);
      // Don't throw - this is a background sync operation
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No authenticated user found");

      console.log("Current webhooks state:", webhooks);
      console.log("User ID:", user.id);

      // Convert webhooks to the database format: [{object_type: url}]
      const webhooksData: WebhookData = webhooks.map((webhook) => {
        const entry: Record<string, string> = {};
        entry[webhook.object_type] = webhook.url;
        return entry;
      });

      console.log("Saving webhooks to saas_details:", webhooksData);
      console.log("Webhooks data length:", webhooksData.length);

      // Update saas_details with webhooks
      const { data: updateData, error: updateError } = await supabase
        .from("saas_details")
        .update({
          webhooks: webhooksData.length > 0 ? webhooksData : null,
        })
        .eq("id", user.id)
        .select();

      if (updateError) {
        console.error("Error updating saas_details.webhooks:", updateError);
        throw updateError;
      }

      // Note: updateData might be empty due to RLS policies on .select() after UPDATE
      // This is okay - we'll verify the update persisted separately
      if (updateData && updateData.length > 0) {
        console.log("Successfully updated saas_details.webhooks:", updateData);
      } else {
        console.log("Update completed, verifying persistence...");
      }

      // Verify the update persisted
      const { data: verifyData, error: verifyError } = await supabase
        .from("saas_details")
        .select("webhooks")
        .eq("id", user.id)
        .single();

      if (verifyError) {
        console.error("Error verifying update:", verifyError);
        throw new Error("Update may have failed. Please refresh and try again.");
      }

      // Verify the webhooks match what we tried to save
      const savedWebhooks = verifyData?.webhooks;
      console.log("Verified webhooks after update:", savedWebhooks);
      
      // Check if the saved webhooks match what we're trying to save
      if (webhooksData.length > 0) {
        const savedWebhooksArray = Array.isArray(savedWebhooks) ? savedWebhooks : [];
        const savedKeys = savedWebhooksArray.map((entry: Record<string, string>) => Object.keys(entry)[0]).sort();
        const expectedKeys = webhooksData.map((entry) => Object.keys(entry)[0]).sort();
        
        if (JSON.stringify(savedKeys) !== JSON.stringify(expectedKeys)) {
          console.warn("Webhook keys mismatch. Expected:", expectedKeys, "Got:", savedKeys);
          // Don't throw - the update might still be in progress or there might be a timing issue
        }
      } else if (savedWebhooks !== null) {
        console.warn("Expected webhooks to be null, but got:", savedWebhooks);
      }

      // Sync webhooks to all active integrations
      await syncWebhooksToIntegrations();

      setError("Webhooks saved successfully");
      setTimeout(() => setError(""), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save webhooks");
      console.error("Error saving webhooks:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleApplyFilters = () => {
    loadHistory();
  };

  const handleClearFilters = () => {
    setHistoryFilters({
      entityType: "",
      statusCode: "",
      startDate: "",
      endDate: "",
      hotelCode: "",
    });
    setTimeout(() => loadHistory(), 100);
  };

  const toggleHistoryExpansion = (id: string) => {
    setExpandedHistoryId(expandedHistoryId === id ? null : id);
  };

  const getStatusBadgeClass = (statusCode: number | null) => {
    if (!statusCode) return "bg-gray-100 text-gray-800";
    if (statusCode >= 200 && statusCode < 300) return "bg-green-100 text-green-800";
    if (statusCode >= 400) return "bg-red-100 text-red-800";
    return "bg-yellow-100 text-yellow-800";
  };

  if (loading) {
    return (
      <div className="extranet-container">
        <div className="extranet-container-loading">
          <div className="px-6 py-4 text-center text-sm text-gray-500">
            Loading webhooks...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="extranet-container">
      <div className="extranet-container-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Globe className="h-6 w-6 text-indigo-600" />
            <h2 className="text-xl font-bold text-gray-600">Webhooks</h2>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary inline-flex items-center"
          >
            <Check className="h-4 w-4 mr-2" />
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>

      <div className="extranet-container-body">
        {error && (
          <div
            className={`mb-4 p-4 rounded-md ${
              error.includes("successfully") ? "bg-green-50" : "bg-red-50"
            }`}
          >
            <p className="text-sm">{error}</p>
          </div>
        )}

        {/* Configuration Section */}
        <div className="mb-8">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Webhook Configuration</h3>
          <p className="text-sm text-gray-500 mb-4">
            Configure webhook URLs for different entity types. Webhooks will be sent when events occur for these entities.
          </p>

          {availableObjectTypes.length > 0 && !isAddingWebhook && (
            <button
              onClick={handleAddWebhook}
              className="btn-secondary inline-flex items-center mb-4"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Webhook
            </button>
          )}

          {/* Add Webhook Form */}
          {isAddingWebhook && (
            <div className="mb-6 p-4 border border-indigo-100 rounded-lg bg-indigo-50">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Add New Webhook</h4>

              <div className="grid grid-cols-1 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Entity Type
                  </label>
                  <select
                    value={newWebhook.object_type}
                    onChange={(e) =>
                      setNewWebhook({
                        ...newWebhook,
                        object_type: e.target.value,
                      })
                    }
                    className="w-full p-2 border border-gray-300 rounded-md"
                  >
                    {availableObjectTypes.length === 0 ? (
                      <option value="">No available entity types</option>
                    ) : (
                      availableObjectTypes.map((scope: Scope) => (
                        <option key={scope.code} value={scope.code}>
                          {scope.name}
                        </option>
                      ))
                    )}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Webhook URL
                  </label>
                  <input
                    type="url"
                    value={newWebhook.url}
                    onChange={(e) =>
                      setNewWebhook({ ...newWebhook, url: e.target.value })
                    }
                    placeholder="https://example.com/webhook"
                    className="w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleCancelAddWebhook}
                  className="btn-secondary text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveWebhook}
                  className="btn-primary text-sm"
                  disabled={!newWebhook.object_type || !newWebhook.url}
                >
                  Add Webhook
                </button>
              </div>
            </div>
          )}

          {/* Webhooks List */}
          {webhooks.length > 0 ? (
            <div className="grid grid-cols-1 gap-3">
              {webhooks.map((webhook, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  {editingWebhookIndex === index ? (
                    <div className="w-full">
                      <div className="font-medium text-sm text-gray-800 mb-2">
                        {scopes.find((scope) => scope.code === webhook.object_type)?.name ||
                          webhook.object_type}
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="url"
                          value={editingWebhookUrl}
                          onChange={(e) => setEditingWebhookUrl(e.target.value)}
                          className="flex-1 p-2 text-sm border border-gray-300 rounded-md"
                          placeholder="https://example.com/webhook"
                        />
                        <div className="flex space-x-2">
                          <button
                            onClick={handleCancelEditWebhook}
                            className="p-1 text-gray-500 hover:text-gray-700"
                            title="Cancel"
                          >
                            <X className="h-4 w-4" />
                          </button>
                          <button
                            onClick={handleSaveEditWebhook}
                            className="p-1 text-green-500 hover:text-green-700"
                            title="Save"
                            disabled={!editingWebhookUrl.trim()}
                          >
                            <Check className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div>
                        <div className="font-medium text-sm text-gray-800">
                          {scopes.find((scope) => scope.code === webhook.object_type)?.name ||
                            webhook.object_type}
                        </div>
                        <div className="text-sm text-gray-500 truncate max-w-md">
                          {webhook.url}
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditWebhook(index)}
                          className="text-blue-500 hover:text-blue-700"
                          title="Edit"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="h-4 w-4"
                          >
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                          </svg>
                        </button>
                        <button
                          onClick={() => handleRemoveWebhook(index)}
                          className="text-red-500 hover:text-red-700"
                          title="Delete"
                        >
                          <Trash className="h-4 w-4" />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 border border-dashed rounded-lg">
              <p className="text-sm text-gray-500">
                No webhooks configured.{" "}
                {availableObjectTypes.length > 0
                  ? 'Click "Add Webhook" to create one.'
                  : "Select scopes in Functional Scope to enable webhook configuration."}
              </p>
            </div>
          )}
        </div>

        {/* History Section */}
        <div className="border-t pt-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Webhook History</h3>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="btn-secondary inline-flex items-center text-sm"
            >
              <Filter className="h-4 w-4 mr-2" />
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>
          </div>

          {/* Filters */}
          {showFilters && (
            <div className="mb-4 p-4 border rounded-lg bg-gray-50">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Entity Type
                  </label>
                  <select
                    value={historyFilters.entityType}
                    onChange={(e) =>
                      setHistoryFilters({ ...historyFilters, entityType: e.target.value })
                    }
                    className="w-full p-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="">All Types</option>
                    {scopes
                      .filter((s) => s.code.toLowerCase() !== "webhooks")
                      .map((scope) => (
                        <option key={scope.code} value={scope.code}>
                          {scope.name}
                        </option>
                      ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hotel Code
                  </label>
                  <select
                    value={historyFilters.hotelCode}
                    onChange={(e) =>
                      setHistoryFilters({ ...historyFilters, hotelCode: e.target.value })
                    }
                    className="w-full p-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="">All Hotels</option>
                    {availableHotelCodes.map((code) => (
                      <option key={code} value={code}>
                        {code}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status Code
                  </label>
                  <select
                    value={historyFilters.statusCode}
                    onChange={(e) =>
                      setHistoryFilters({ ...historyFilters, statusCode: e.target.value })
                    }
                    className="w-full p-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="">All Status</option>
                    <option value="200">200 - Success</option>
                    <option value="400">400 - Bad Request</option>
                    <option value="401">401 - Unauthorized</option>
                    <option value="403">403 - Forbidden</option>
                    <option value="404">404 - Not Found</option>
                    <option value="500">500 - Server Error</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date
                  </label>
                  <input
                    type="datetime-local"
                    value={historyFilters.startDate}
                    onChange={(e) =>
                      setHistoryFilters({ ...historyFilters, startDate: e.target.value })
                    }
                    className="w-full p-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date
                  </label>
                  <input
                    type="datetime-local"
                    value={historyFilters.endDate}
                    onChange={(e) =>
                      setHistoryFilters({ ...historyFilters, endDate: e.target.value })
                    }
                    className="w-full p-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3">
                <button onClick={handleClearFilters} className="btn-secondary text-sm">
                  Clear Filters
                </button>
                <button onClick={handleApplyFilters} className="btn-primary text-sm">
                  Apply Filters
                </button>
              </div>
            </div>
          )}

          {/* History Table */}
          {historyLoading ? (
            <div className="text-center py-8">
              <p className="text-sm text-gray-500">Loading history...</p>
            </div>
          ) : history.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="table-header">Timestamp</th>
                    <th className="table-header">Entity Type</th>
                    <th className="table-header">Hotel Code</th>
                    <th className="table-header">URL</th>
                    <th className="table-header">Status</th>
                    <th className="table-header">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {history.map((item) => (
                    <>
                      <tr key={item.id} className="table-row">
                        <td className="table-cell">
                          {new Date(item.created_at).toLocaleString()}
                        </td>
                        <td className="table-cell">
                          {scopes.find((s) => s.code === item.entity_type)?.name ||
                            item.entity_type}
                        </td>
                        <td className="table-cell">
                          {item.hotel_code || "N/A"}
                        </td>
                        <td className="table-cell">
                          <div className="truncate max-w-xs" title={item.webhook_url}>
                            {item.webhook_url}
                          </div>
                        </td>
                        <td className="table-cell">
                          <span
                            className={`status-badge ${getStatusBadgeClass(item.status_code)}`}
                          >
                            {item.status_code || "N/A"}
                            {item.error_message && " (Error)"}
                          </span>
                        </td>
                        <td className="table-cell">
                          <button
                            onClick={() => toggleHistoryExpansion(item.id)}
                            className="text-indigo-600 hover:text-indigo-800"
                          >
                            {expandedHistoryId === item.id ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </button>
                        </td>
                      </tr>
                      {expandedHistoryId === item.id && (
                        <tr>
                          <td colSpan={6} className="px-6 py-4 bg-gray-50">
                            <div className="space-y-3">
                              <div>
                                <h4 className="text-sm font-medium text-gray-700 mb-2">
                                  Payload
                                </h4>
                                <pre className="bg-white p-3 rounded border text-xs overflow-auto max-h-64">
                                  {JSON.stringify(item.payload, null, 2)}
                                </pre>
                              </div>
                              {item.response_body && (
                                <div>
                                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                                    Response Body
                                  </h4>
                                  <pre className="bg-white p-3 rounded border text-xs overflow-auto max-h-32">
                                    {item.response_body}
                                  </pre>
                                </div>
                              )}
                              {item.error_message && (
                                <div>
                                  <h4 className="text-sm font-medium text-red-700 mb-2">
                                    Error Message
                                  </h4>
                                  <p className="text-sm text-red-600">{item.error_message}</p>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 border border-dashed rounded-lg">
              <p className="text-sm text-gray-500">No webhooks sent yet.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

